import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  Filter, 
  Eye, 
  MessageSquare, 
  Clock, 
  Calendar,
  Mail,
  Phone,
  Activity,
  ChevronRight,
  FileText,
  Video,
  User
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  first_seen: string;
  last_seen: string;
  total_sessions: number;
  status: string;
}

interface CustomerSession {
  id: string;
  session_id: string;
  session_type: string;
  agent_name: string;
  notes: string;
  tags: string[];
  session_duration_minutes: number;
  session_date: string;
}

export const CustomersPage: React.FC = () => {
  const { currentWorkspace, getCurrentOrganizationId } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerSessions, setCustomerSessions] = useState<CustomerSession[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get organization ID from auth context
  const orgId = getCurrentOrganizationId();
  
  console.log('Current Workspace:', currentWorkspace);
  console.log('Organization ID:', orgId);

  // Fetch all customers with recent activity
  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔍 CUSTOMERS: Fetching customers for org:', orgId);
      
      // Get API key from localStorage (same pattern as AttorneyDashboard)
      // Note: Backend doesn't currently require auth, but we include it if available
      const storedKey = localStorage.getItem(`attorney_api_key_${orgId}`);
      
      // Since we don't have a "get all customers" endpoint, let's search with a broad query
      // Note: Backend currently expects org_id param, so we include it for now
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (storedKey) {
        headers['Authorization'] = `Bearer ${storedKey}`;
      }
      
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/customers/search?q=@&org_id=${encodeURIComponent(orgId)}`, {
        headers
      });
      
      console.log('📡 CUSTOMERS: API Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 CUSTOMERS: Received data:', data);
        setCustomers(data.customers || []);
      } else {
        const errorText = await response.text();
        console.error('🔴 CUSTOMERS: API Error:', errorText);
        throw new Error(`Failed to fetch customers: ${response.status}`);
      }
    } catch (error) {
      console.error('🔴 CUSTOMERS: Error fetching customers:', error);
      setError('Failed to load customers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Search customers
  const searchCustomers = async (query: string) => {
    if (!query || query.length < 2) {
      fetchCustomers();
      return;
    }

    const storedKey = localStorage.getItem(`attorney_api_key_${orgId}`);
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (storedKey) {
      headers['Authorization'] = `Bearer ${storedKey}`;
    }

    setLoading(true);
    try {
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/customers/search?q=${encodeURIComponent(query)}&org_id=${encodeURIComponent(orgId)}`, {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch customer session history
  const fetchCustomerHistory = async (customer: Customer) => {
    const storedKey = localStorage.getItem(`attorney_api_key_${orgId}`);
    if (!storedKey) {
      console.error('No API key available for fetching customer history');
      return;
    }

    setLoadingHistory(true);
    try {
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/customers/${encodeURIComponent(customer.email)}/history`, {
        headers: {
          'Authorization': `Bearer ${storedKey}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomerSessions(data.recent_sessions || []);
      }
    } catch (error) {
      console.error('Error fetching customer history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    fetchCustomerHistory(customer);
  };

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      searchCustomers(query);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'blocked': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Users className="mr-3" size={32} />
                Customers
              </h1>
              <p className="text-gray-600 mt-2">
                Manage customers who have requested video calls
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center">
                  <Activity size={16} className="text-green-600 mr-2" />
                  <span className="font-medium text-gray-900">{customers.length}</span>
                  <span className="text-gray-600 ml-1">Total Customers</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Customer List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              {/* Search and Filters */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center space-x-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder="Search customers by name or email..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <button className="flex items-center px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                    <Filter size={16} className="mr-2" />
                    Filter
                  </button>
                </div>
              </div>

              {/* Customer List */}
              <div className="divide-y divide-gray-200">
                {loading ? (
                  <div className="p-6 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading customers...</p>
                  </div>
                ) : error ? (
                  <div className="p-6 text-center">
                    <p className="text-red-600">{error}</p>
                    <button
                      onClick={fetchCustomers}
                      className="mt-2 text-blue-600 hover:text-blue-700"
                    >
                      Try Again
                    </button>
                  </div>
                ) : customers.length === 0 ? (
                  <div className="p-6 text-center">
                    <Users size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">No customers found</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {searchQuery ? 'Try a different search term' : 'Customers will appear here after they request video calls'}
                    </p>
                  </div>
                ) : (
                  customers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => handleCustomerSelect(customer)}
                      className={`p-6 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedCustomer?.id === customer.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <User size={20} className="text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{customer.name}</h3>
                            <div className="flex items-center space-x-3 mt-1">
                              <span className="text-sm text-gray-600 flex items-center">
                                <Mail size={14} className="mr-1" />
                                {customer.email}
                              </span>
                              {customer.phone && (
                                <span className="text-sm text-gray-600 flex items-center">
                                  <Phone size={14} className="mr-1" />
                                  {customer.phone}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-3 mt-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(customer.status)}`}>
                                {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                              </span>
                              <span className="text-xs text-gray-500">
                                {customer.total_sessions} session{customer.total_sessions !== 1 ? 's' : ''}
                              </span>
                              <span className="text-xs text-gray-500">
                                Last seen: {formatDate(customer.last_seen)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <ChevronRight size={20} className="text-gray-400" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Customer Details Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow">
              {selectedCustomer ? (
                <div>
                  {/* Customer Header */}
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <User size={24} className="text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">{selectedCustomer.name}</h2>
                        <p className="text-gray-600">{selectedCustomer.email}</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${getStatusColor(selectedCustomer.status)}`}>
                          {selectedCustomer.status.charAt(0).toUpperCase() + selectedCustomer.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Customer Stats */}
                  <div className="p-6 border-b border-gray-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{selectedCustomer.total_sessions}</div>
                        <div className="text-sm text-gray-600">Total Sessions</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {Math.ceil((new Date().getTime() - new Date(selectedCustomer.first_seen).getTime()) / (1000 * 60 * 60 * 24))}
                        </div>
                        <div className="text-sm text-gray-600">Days as Customer</div>
                      </div>
                    </div>
                    <div className="mt-4 space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">First seen:</span>
                        <span className="text-gray-900">{formatDate(selectedCustomer.first_seen)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Last seen:</span>
                        <span className="text-gray-900">{formatDate(selectedCustomer.last_seen)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Session History */}
                  <div className="p-6">
                    <h3 className="font-medium text-gray-900 mb-4 flex items-center">
                      <FileText size={16} className="mr-2" />
                      Recent Sessions
                    </h3>
                    
                    {loadingHistory ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="text-sm text-gray-600 mt-2">Loading history...</p>
                      </div>
                    ) : customerSessions.length === 0 ? (
                      <p className="text-sm text-gray-600 text-center py-4">No session history found</p>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {customerSessions.map((session) => (
                          <div key={session.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <Video size={14} className="text-blue-600" />
                                <span className="text-sm font-medium text-gray-900">
                                  {session.session_type.charAt(0).toUpperCase() + session.session_type.slice(1)} Session
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">{session.session_duration_minutes}min</span>
                            </div>
                            
                            {session.notes && (
                              <div className="text-sm text-gray-700 bg-white p-2 rounded border mb-2">
                                {session.notes}
                              </div>
                            )}
                            
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>Agent: {session.agent_name}</span>
                              <span>{formatDate(session.session_date)}</span>
                            </div>

                            {session.tags && session.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {session.tags.map((tag, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center">
                  <Users size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">Select a customer to view details</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Click on any customer from the list to see their session history and details
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};